import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg, fmtNum, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Layers, Plus, Trash2, TrendingUp, Fuel } from "lucide-react";

const CATS = ["Freight", "Duty", "Insurance", "Port Charges", "Financing", "Inspection", "Storage", "Other"];

function Stat({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`font-head text-2xl font-bold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function LandedCost() {
  const { hasPerm } = useAuth();
  const [spas, setSpas] = useState([]);
  const [spaId, setSpaId] = useState("");
  const [detail, setDetail] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [form, setForm] = useState({ category: "Freight", description: "", amount: "", currency: "USD" });
  const [busy, setBusy] = useState(false);
  const [floor, setFloor] = useState(null);
  const [scanning, setScanning] = useState(false);
  const canManage = hasPerm("cost.manage");
  const canScan = hasPerm("recon.manage");

  const loadAnalysis = useCallback(() => api.get("/landed-cost/analysis").then(({ data }) => setAnalysis(data)).catch(() => setAnalysis(null)), []);
  const loadDetail = useCallback((id) => { if (!id) return; api.get(`/spas/${id}/landed-cost`).then(({ data }) => setDetail(data)).catch((e) => toast.error(errMsg(e))); }, []);

  useEffect(() => {
    api.get("/spas").then(({ data }) => { setSpas(data); if (data.length) { setSpaId(data[0].id); loadDetail(data[0].id); } }).catch(() => {});
    loadAnalysis();
    api.get("/settings").then(({ data }) => setFloor(data.margin_floor_pct)).catch(() => {});
  }, [loadAnalysis, loadDetail]);

  const scanMargins = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/landed-cost/margin-scan");
      if (data.breaches > 0) toast.warning(`${data.breaches} product(s) below the ${data.floor_pct}% floor · ${data.exceptions_raised} exception(s) raised`);
      else toast.success(`All products meet the ${data.floor_pct}% margin floor`);
      loadAnalysis();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setScanning(false); }
  };

  const onSelect = (id) => { setSpaId(id); setDetail(null); loadDetail(id); };

  const addCost = async () => {
    if (!form.amount) { toast.error("Amount is required"); return; }
    setBusy(true);
    try {
      await api.post(`/spas/${spaId}/costs`, { ...form, amount: Number(form.amount) });
      toast.success("Cost component added");
      setForm({ category: "Freight", description: "", amount: "", currency: "USD" });
      loadDetail(spaId); loadAnalysis();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const delCost = async (cid) => {
    try { await api.delete(`/costs/${cid}`); loadDetail(spaId); loadAnalysis(); toast.success("Removed"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Layers className="h-6 w-6 text-[#F47920]" /> Landed Cost & Margin</h1>
        <p className="text-sm text-slate-500 mt-0.5">Allocate freight, duty, insurance and fees across each agreement so every litre carries its true landed cost and true margin. All figures normalized to USD.</p>
      </div>

      {analysis && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <Stat label="Total Landed Cost" value={fmtMoney(analysis.totals.total_landed_cost)} />
          <Stat label="Total Revenue (Completed)" value={fmtMoney(analysis.totals.total_revenue)} />
          <Stat label="Total Margin" value={fmtMoney(analysis.totals.total_margin)} tone={analysis.totals.total_margin >= 0 ? "text-emerald-600" : "text-rose-600"} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4"><Fuel className="h-5 w-5 text-[#12346B]" /><h2 className="font-head font-semibold text-slate-900">SPA Landed Cost Breakdown</h2></div>
          <div className="mb-4">
            <Label className="text-xs text-slate-600">Select Agreement (SPA)</Label>
            <Select value={spaId} onValueChange={onSelect}>
              <SelectTrigger data-testid="lc-spa-select"><SelectValue placeholder="Choose SPA" /></SelectTrigger>
              <SelectContent className="bg-white z-[60]">
                {spas.map((s) => <SelectItem key={s.id} value={s.id}>{s.spa_no} · {s.supplier_name} · {s.product_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {!detail ? <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2"><div className="text-[10px] uppercase text-slate-400">Product Cost</div><div className="font-semibold text-slate-800">{fmtMoney(detail.product_cost)}</div></div>
                <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2"><div className="text-[10px] uppercase text-slate-400">+ Landed Components</div><div className="font-semibold text-slate-800">{fmtMoney(detail.components_total)}</div></div>
                <div className="rounded-md bg-[#12346B]/5 border border-[#12346B]/20 px-3 py-2"><div className="text-[10px] uppercase text-[#12346B]">Total Landed Cost</div><div className="font-bold text-[#12346B]">{fmtMoney(detail.total_landed_cost)}</div></div>
                <div className="rounded-md bg-[#F47920]/10 border border-[#F47920]/30 px-3 py-2"><div className="text-[10px] uppercase text-[#F47920]">Landed Cost / Gallon</div><div className="font-bold text-[#F47920]" data-testid="lc-per-unit">${fmtNum(detail.landed_cost_per_unit, 4)}</div></div>
              </div>
              <div className="text-[11px] text-slate-400 mb-3">Received basis: {fmtNum(detail.received_qty)} gal</div>

              <Table className="pf-table mb-3">
                <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Category", "Description", "Amount", "Ccy", ""].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2 px-3">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {(detail.components || []).length === 0 ? <TableRow><TableCell colSpan={5} className="py-6 text-center text-slate-400 text-sm">No landed-cost components yet.</TableCell></TableRow>
                    : detail.components.map((c) => (
                      <TableRow key={c.id} data-testid={`lc-comp-${c.id}`}>
                        <TableCell className="py-2 px-3 text-sm font-medium">{c.category}</TableCell>
                        <TableCell className="py-2 px-3 text-sm text-slate-500">{c.description || "—"}</TableCell>
                        <TableCell className="py-2 px-3 text-sm tabular">{fmtNum(c.amount, 2)}</TableCell>
                        <TableCell className="py-2 px-3 text-xs mono">{c.currency}</TableCell>
                        <TableCell className="py-2 px-3 text-right">{canManage && <button onClick={() => delCost(c.id)} className="text-slate-400 hover:text-rose-600" data-testid={`lc-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              {canManage && spaId && (
                <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
                      <SelectTrigger data-testid="lc-cat"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white z-[60]">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))} data-testid="lc-amount" /></div>
                  <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Description</Label><Input value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} data-testid="lc-desc" /></div>
                  <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Currency</Label><Input className="uppercase" value={form.currency} onChange={(e) => setForm((s) => ({ ...s, currency: e.target.value.toUpperCase() }))} data-testid="lc-ccy" /></div>
                  <div className="col-span-2"><Button className="w-full bg-[#12346B] hover:bg-[#12346B]/90" onClick={addCost} disabled={busy} data-testid="btn-add-cost">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add Cost Component</Button></div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[#12346B]" /><h2 className="font-head font-semibold text-slate-900">Product Margin (True Landed vs Realized)</h2></div>
            {canScan && <Button size="sm" variant="outline" onClick={scanMargins} disabled={scanning} data-testid="btn-scan-margins">{scanning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-1" />} Scan Margins{floor != null ? ` (floor ${floor}%)` : ""}</Button>}
          </div>
          {!analysis ? <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
            <div className="overflow-x-auto">
              <Table className="pf-table">
                <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Product", "Landed/gal", "Sell/gal", "Margin/gal", "Sold (gal)", "Margin", "%"].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2 px-3">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {analysis.per_product.length === 0 ? <TableRow><TableCell colSpan={7} className="py-6 text-center text-slate-400 text-sm">No data.</TableCell></TableRow>
                    : analysis.per_product.map((p) => (
                      <TableRow key={p.product_name} data-testid={`margin-${p.product_name.replace(/\s/g, "-")}`} className={p.sold_qty > 0 && floor != null && p.margin_pct < floor ? "bg-rose-50/60" : ""}>
                        <TableCell className="py-2 px-3 text-[13px] font-medium">{p.product_name}{p.sold_qty > 0 && floor != null && p.margin_pct < floor && <span className="ml-1.5 text-[10px] font-semibold text-rose-600" data-testid={`below-floor-${p.product_name.replace(/\s/g, "-")}`}>below floor</span>}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">${fmtNum(p.landed_cost_per_unit, 4)}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">${fmtNum(p.avg_sell_price, 4)}</TableCell>
                        <TableCell className={`py-2 px-3 text-[13px] tabular font-semibold ${p.margin_per_unit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>${fmtNum(p.margin_per_unit, 4)}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">{fmtNum(p.sold_qty)}</TableCell>
                        <TableCell className={`py-2 px-3 text-[13px] tabular ${p.margin_total >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtMoney(p.margin_total)}</TableCell>
                        <TableCell className="py-2 px-3 text-[13px] tabular">{fmtNum(p.margin_pct, 1)}%</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
